/* PHEFEE — global.js
   Minimal vanilla JS for variant selection + cart drawer.
   Relies on data-* hooks rendered by the Liquid templates.
   Build: btt-eased-2 2026-08-14 */

(function () {
  'use strict';

  /* Format a price given in CENTS (this store's variant.price is in cents). */
  function formatMoney(cents) {
    var value = (Number(cents) / 100).toFixed(2);
    if (window.themeMoneyFormat) {
      return window.themeMoneyFormat
        .replace('{{amount_with_comma_separator}}', value)
        .replace('{{amount_no_decimals}}', Math.round(Number(cents) / 100))
        .replace('{{amount}}', value);
    }
    return '$' + value;
  }

  /* Buttons ship as `<button><span class="btn__spinner"></span><span class="btn__label">…`,
     so writing to btn.textContent wipes out the spinner and the label hook —
     which broke the loading animation and every later .btn__label lookup.
     Always route text updates through the label span when it exists. */
  function setBtnLabel(btn, text) {
    if (!btn) return;
    var label = btn.querySelector('.btn__label');
    if (label) label.textContent = text;
    else btn.textContent = text;
  }

  /* ---------------- PDP CTA availability (all breakpoints) ----------------
     Single helper so the desktop form buttons, the desktop sticky bar and the
     mobile action TabBar never disagree about whether the current variant can
     actually be bought. The mobile bar is a separate DOM subtree from the form,
     so it would otherwise keep shouting "add to cart" on a sold-out size. */
  function syncPdpCtas(available) {
    var soldOut = themeStrings.soldOut;

    /* Mobile PDP action TabBar: "add to cart" collapses to the sold-out label,
       "Buy now" keeps its wording but becomes non-interactive. The Amazon link
       is deliberately untouched — it has its own stock. */
    var bar = document.querySelector('[data-pdp-actionbar]');
    if (bar) {
      var add = bar.querySelector('[data-pdp-add]');
      if (add) {
        add.disabled = !available;
        if (available) add.removeAttribute('aria-disabled');
        else add.setAttribute('aria-disabled', 'true');
        var addLabel = add.querySelector('.pdp-actionbar__label');
        if (addLabel) addLabel.textContent = available ? 'Add to cart' : soldOut;
      }
      var buy = bar.querySelector('[data-pdp-buy]');
      if (buy) {
        buy.disabled = !available;
        if (available) buy.removeAttribute('aria-disabled');
        else buy.setAttribute('aria-disabled', 'true');
      }
    }

    /* Desktop sticky bar — mirrors the form's add-to-cart button. */
    var sticky = document.querySelector('[data-sticky-addbar] [data-sticky-add]');
    if (sticky) {
      sticky.disabled = !available;
      sticky.textContent = available ? themeStrings.addToCart : soldOut;
    }
  }

  /* ---------------- Mobile: keep the product image in view when choosing ----------------
     On a phone the gallery is ~448px tall and the option pickers sit ~350px below it,
     so by the time a shopper taps a size the image has scrolled off-screen — they pick
     a variant and see nothing change. Nudge the page so the LOWER PART of the gallery
     and the option rows share one screen. Deliberately skipped when the gallery is
     still visible (e.g. nothing scrolled yet) so it never fights the shopper. */
  function revealVariantImage() {
    if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return;
    var gallery = document.querySelector('.product__gallery-wrap');
    var picker = document.querySelector('.variant-picker');
    if (!gallery || !picker) return;

    var vh = window.innerHeight;
    var galleryBottom = gallery.getBoundingClientRect().bottom;
    if (galleryBottom >= 80) return; // image still on screen — leave the page alone

    // Land with the picker's top ~190px above the fold, which shows the option rows
    // and leaves the rest of the screen to the bottom of the photo.
    var delta = picker.getBoundingClientRect().top - vh + 190;
    if (Math.abs(delta) < 20) return;
    var target = window.pageYOffset + delta;
    window.scrollTo({ top: target < 0 ? 0 : target, behavior: 'smooth' });
  }

  /* ---------------- Desktop: align the spec label column ----------------
     The label column sizes to its content so long values (Material) are never
     truncated — but then each row starts at a different x. Measure once and pin all
     of them to the widest, which keeps the option groups in a clean line. */
  function alignSpecLabels() {
    var picker = document.querySelector('#product-form .variant-picker');
    if (!picker) return;
    var labels = Array.prototype.slice.call(picker.querySelectorAll('.variant-picker__label'));
    if (!labels.length) return;
    labels.forEach(function (l) { l.style.minWidth = ''; });
    if (!window.matchMedia || !window.matchMedia('(min-width: 750px)').matches) return;
    var max = 0;
    labels.forEach(function (l) { if (l.scrollWidth > max) max = l.scrollWidth; });
    if (max) labels.forEach(function (l) { l.style.minWidth = max + 'px'; });
  }

  /* ---------------- Variant picker ---------------- */
  function initVariantPickers() {
    document.querySelectorAll('#product-form').forEach(function (form) {
      var productJSON = form.querySelector('[data-product-json]');
      if (!productJSON) return;
      var product;
      try { product = JSON.parse(productJSON.textContent); } catch (e) { return; }

      // Display elements (price / sku / compare / gallery) live OUTSIDE the
      // <form>, so scope our lookups to the whole product section instead.
      var root = form.closest('.product') || document;

      var inputs = form.querySelectorAll('input[name^="options["]');

      function getSelectedOptions() {
        var opts = {};
        inputs.forEach(function (input) {
          if (!input.checked) return;
          var m = input.name.match(/options\[(.+)\]/);
          if (m) opts[m[1]] = input.value;
        });
        return opts;
      }

      function findVariant(selected) {
        return product.variants.find(function (v) {
          return product.options.every(function (opt) {
            return v.options[product.options.indexOf(opt)] === selected[opt];
          });
        });
      }

      function update() {
        var selected = getSelectedOptions();
        var variant = findVariant(selected);
        // Single-variant products ("Default Title") expose options = ["Title"] yet
        // render no option inputs, so findVariant() can never match. Fall back to
        // the only variant — otherwise a sold-out one-piece read "Unavailable" on
        // desktop while the mobile bar correctly said "Sold out".
        if (!variant && product.variants.length === 1) variant = product.variants[0];

        // Refresh the "Color: X" / "Size: Y" labels next to each option name.
        product.options.forEach(function (name) {
          var label = root.querySelector('[data-option-selected="' + name + '"]');
          if (label && selected[name] != null) label.textContent = selected[name];
        });

        // update add-to-cart button
        var atc = form.querySelector('[data-add-to-cart]');
        var priceEl = root.querySelector('[data-product-price]');
        var compareEl = root.querySelector('[data-product-compare-price]');
        var skuEl = root.querySelector('[data-product-sku]');

        if (variant) {
          var idInput = form.querySelector('input[name="id"]');
          if (idInput) idInput.value = variant.id;
          if (priceEl && variant.price != null) {
            priceEl.innerHTML = formatMoney(variant.price);
          }
          // compare-at (sale) price — show only when it's higher than current price
          if (compareEl) {
            var cmp = Number(variant.compare_at_price) || 0;
            var price = Number(variant.price) || 0;
            var onSale = cmp > price;
            compareEl.textContent = formatMoney(cmp);
            compareEl.hidden = !onSale;
            var priceWrap = priceEl ? priceEl.closest('.price') : null;
            if (priceWrap) priceWrap.classList.toggle('price--on-sale', onSale);
          }
          if (skuEl && variant.sku) skuEl.textContent = variant.sku;

          // Swap the main gallery image to the variant's own image. When the
          // variant has no image assigned (e.g. the default colour's first
          // variant), fall back to the product's main image so the user can
          // always switch *back* to the main colour.
          var newImg = variant.featured_image || product.main_image || null;
          if (newImg) {
            window.dispatchEvent(new CustomEvent('gallery:set-image', { detail: { src: newImg } }));
          }

          if (atc) {
            atc.disabled = !variant.available;
            setBtnLabel(atc, variant.available ? themeStrings.addToCart : themeStrings.soldOut);
          }
          // keep the desktop Buy now button in sync with variant availability
          var bnPdp = form.querySelector('[data-buy-now-pdp]');
          if (bnPdp) bnPdp.disabled = !variant.available;

          // …and mirror that across the mobile action TabBar + desktop sticky bar.
          syncPdpCtas(variant.available);

          if (variant.available) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            history.replaceState(null, '', url);
          }
        } else {
          // No variant matches the selected combination — nothing is buyable.
          if (atc) {
            atc.disabled = true;
            setBtnLabel(atc, themeStrings.unavailable);
          }
          var bnPdpNone = form.querySelector('[data-buy-now-pdp]');
          if (bnPdpNone) bnPdpNone.disabled = true;
          syncPdpCtas(false);
        }
      }

      function syncAria() {
        // 选中态写到 .variant-value label 上，与 CSS [aria-checked="true"] 契约对齐
        form.querySelectorAll('.variant-value').forEach(function (label) {
          var inp = label.querySelector('[data-variant-input]');
          label.setAttribute('aria-checked', inp && inp.checked ? 'true' : 'false');
        });
      }

      inputs.forEach(function (input) {
        input.addEventListener('change', function () {
          syncAria();
          update();
          // Mobile only: bring the photo back so the new variant is actually seen.
          revealVariantImage();
        });
      });

      syncAria();
      update();
    });
  }

  /* ---------------- Cart drawer ---------------- */
  var cartDrawer, cartBackdrop, cartBody, cartCountEls;

  function openCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    if (cartBackdrop) { cartBackdrop.hidden = false; void cartBackdrop.offsetWidth; cartBackdrop.classList.add('is-open'); }
    document.body.classList.add('cart-open');
  }
  function closeCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    if (cartBackdrop) cartBackdrop.classList.remove('is-open');
    document.body.classList.remove('cart-open');
    setTimeout(function () { if (cartBackdrop && !cartBackdrop.classList.contains('is-open')) cartBackdrop.hidden = true; }, 380);
  }
  function updateCartCount(n) {
    cartCountEls.forEach(function (el) {
      el.textContent = n;
      el.style.display = n > 0 ? '' : 'none';
    });
  }
  function cartMoney(cents) { return formatMoney(cents); }
  function renderCart(cart) {
    if (!cartBody) return;
    // Free shipping on all orders — threshold progress bar removed
    var progress = '<p class="cart-drawer__bar-text">Free shipping on all orders</p>';

    var lines = '';
    cart.items.forEach(function (item) {
      var img = item.image ? '<img src="' + item.image + '" alt="' + (item.product_title || '').replace(/"/g, '') + '" loading="lazy" width="72" height="90">' : '<span class="cd-line__placeholder"></span>';
      var variant = (item.variant_title && item.variant_title !== 'Default Title') ? '<p class="cd-line__variant">' + item.variant_title + '</p>' : '';
      lines += '<div class="cd-line" data-cart-line data-key="' + item.key + '">'
        + '<a class="cd-line__media" href="' + item.url + '">' + img + '</a>'
        + '<div class="cd-line__info">'
        + '<div class="cd-line__top"><a class="cd-line__title" href="' + item.url + '">' + (item.product_title || '') + '</a>'
        + '<span class="cd-line__price">' + cartMoney(item.final_line_price) + '</span></div>'
        + variant
        + '<div class="cd-line__actions">'
        + '<div class="cd-qty"><button type="button" class="cd-qty__btn" data-cart-qty-dec aria-label="Decrease quantity">&minus;</button>'
        + '<input class="cd-qty__input" value="' + item.quantity + '" readonly aria-label="Quantity">'
        + '<button type="button" class="cd-qty__btn" data-cart-qty-inc aria-label="Increase quantity">+</button></div>'
        + '<button type="button" class="cd-line__remove" data-cart-remove>Remove</button>'
        + '</div></div></div>';
    });

    var allProducts = (window.themeRoutes && window.themeRoutes.allProducts) || '/collections/all';
    var empty = cart.item_count === 0
      ? '<div class="cart-drawer__empty" data-cart-empty><p>Your cart is empty.</p><a class="btn btn--secondary" href="' + allProducts + '">Continue shopping</a></div>'
      : '';

    var discApps = cart.cart_level_discount_applications || [];
    var discBlock = '<div class="cart-discount">'
      + '<label class="cart-discount__label" for="drawer-discount-code">Discount code</label>'
      + '<div class="cart-discount__row">'
      + '<input type="text" id="drawer-discount-code" class="cart-discount__input" data-cart-discount-input placeholder="Enter code" autocomplete="off">'
      + '<button type="button" class="btn cart-discount__apply" data-cart-discount-apply>Apply</button>'
      + '</div>'
      + (discApps.length ? '<p class="cart-discount__applied">Code <strong>' + (discApps[0].title || '') + '</strong> applied. <a href="/cart?discount=" data-cart-discount-remove>Remove</a></p>' : '')
      + '</div>';

    var promoBlock = '<div class="cart-promos" data-cart-promos hidden>'
      + '<p class="cart-promos__title">Codes for this cart <span class="cart-promos__note">apply one</span></p>'
      + '<div class="cart-promos__list" data-cart-promos-list></div>'
      + '</div>';

    /* Promos + discount entry live inside a collapsed <details> so they don't push
       Checkout below the fold on a small phone. Read the current open state before we
       blow the markup away, and force it open whenever a cart-level discount is
       applied — otherwise the shopper would have no way to see or remove the code. */
    var toolsEl = cartDrawer.querySelector('.cart-tools');
    var toolsOpen = toolsEl ? toolsEl.open : false;
    var hasDisc = discApps.length > 0;
    /* Stay collapsed by default even when a discount is applied — on a small phone the
       expanded block costs ~190px. The applied state is surfaced on the summary line
       instead ("Discount code · 1 applied"), so nothing is hidden. Only a shopper who
       opened it themselves keeps it open across re-renders. */
    var toolsBlock = '<details class="cart-tools"' + (toolsOpen ? ' open' : '') + '>'
      + '<summary class="cart-tools__summary"><span>Discount code'
      + (hasDisc ? ' &middot; 1 applied' : '')
      + '</span><svg class="cart-tools__chevron" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg></summary>'
      + '<div class="cart-tools__body"><div class="cart-tools__inner">'
      + promoBlock + discBlock
      + '</div></div>'
      + '</details>';

    var foot = cart.item_count > 0
      ? '<div class="cart-drawer__subtotal"><span>Subtotal</span><span>' + cartMoney(cart.total_price) + '</span></div>'
        + '<p class="cart-drawer__note">Shipping &amp; taxes calculated at checkout</p>'
        + '<p class="cart-drawer__ddp">DDP — duties included, nothing extra on delivery.</p>'
        + toolsBlock
        + '<a class="btn btn--full" href="/checkout">Checkout</a>'
        + '<button type="button" class="cart-drawer__continue" data-cart-continue>Continue shopping</button>'
      : '';

    cartBody.innerHTML = empty + lines;
    var footEl = cartDrawer.querySelector('[data-cart-foot]');
    if (footEl) footEl.innerHTML = foot;
    updateCartPromos(cart);
    var progEl = cartDrawer.querySelector('[data-cart-progress]');
    if (progEl) progEl.innerHTML = progress;
    var titleEl = cartDrawer.querySelector('.cart-drawer__title');
    if (titleEl) titleEl.textContent = 'Your cart' + (cart.item_count > 0 ? ' (' + cart.item_count + ')' : '');
  }

  function initCartDrawer() {
    cartDrawer = document.getElementById('cart-drawer');
    cartBackdrop = document.getElementById('cart-backdrop');
    cartCountEls = Array.prototype.slice.call(document.querySelectorAll('[data-cart-count]'));
    if (!cartDrawer) return;
    cartBody = cartDrawer.querySelector('[data-cart-body]');

    document.querySelectorAll('[data-cart-open]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); openCart(); });
    });
    cartDrawer.querySelectorAll('[data-cart-close]').forEach(function (b) { b.addEventListener('click', closeCart); });
    if (cartBackdrop) cartBackdrop.addEventListener('click', closeCart);
    cartDrawer.querySelectorAll('[data-cart-continue]').forEach(function (b) { b.addEventListener('click', closeCart); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeCart(); });

    /* Animate the discount <details> open/close.
       A native <details> hides its content the instant `open` is removed, so a plain
       CSS transition can never run on close. We therefore drive it by hand: keep the
       element open while the grid track collapses to 0fr, then remove `open` once the
       transition has finished. Delegated, so it survives renderCart() rebuilding the
       markup. */
    var TOOLS_MS = 340;
    cartDrawer.addEventListener('click', function (e) {
      var summary = e.target.closest && e.target.closest('.cart-tools__summary');
      if (!summary) return;
      var details = summary.parentNode;
      var body = details.querySelector('.cart-tools__body');
      if (!body) return;
      e.preventDefault();

      if (details.open) {
        body.style.gridTemplateRows = '0fr';
        body.style.opacity = '0';
        window.setTimeout(function () {
          details.open = false;
          body.style.gridTemplateRows = '';
          body.style.opacity = '';
        }, TOOLS_MS);
      } else {
        details.open = true;
        // next frame, so the browser registers the 0fr start value first
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            body.style.gridTemplateRows = '1fr';
            body.style.opacity = '1';
          });
        });
        window.setTimeout(function () {
          body.style.gridTemplateRows = '';
          body.style.opacity = '';
        }, TOOLS_MS + 40);
      }
    });

    var initialCount = parseInt((cartCountEls[0] && cartCountEls[0].textContent) || '0', 10) || 0;
    updateCartCount(initialCount);

    /* On a fresh page load the drawer markup is server-rendered and the promos box
       ships hidden — nothing calls updateCartPromos until an AJAX cart mutation.
       Hydrate it once so the helper survives a refresh + reopen. */
    if (initialCount > 0) {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { updateCartPromos(cart); })
        .catch(function () {});
    }

    cartBody.addEventListener('click', function (e) {
      var lineEl = e.target.closest('[data-cart-line]');
      if (!lineEl) return;
      var key = lineEl.getAttribute('data-key');
      if (e.target.closest('[data-cart-qty-inc]') || e.target.closest('[data-cart-qty-dec]')) {
        var input = lineEl.querySelector('.cd-qty__input');
        var qty = parseInt(input.value, 10) || 1;
        qty = e.target.closest('[data-cart-qty-inc]') ? qty + 1 : qty - 1;
        if (qty < 1) qty = 1;
        updateCartLine(key, qty);
      } else if (e.target.closest('[data-cart-remove]')) {
        updateCartLine(key, 0);
      }
    });

    // Discount code: apply / remove (delegated so it survives AJAX foot rebuilds)
    cartDrawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-discount-apply]')) {
        e.preventDefault();
        var inp = cartDrawer.querySelector('[data-cart-discount-input]');
        applyCartDiscount(inp ? inp.value : '');
      } else if (e.target.closest('[data-cart-discount-remove]')) {
        e.preventDefault();
        applyCartDiscount('');
      } else if (e.target.closest('[data-cart-promo-apply]')) {
        e.preventDefault();
        var promoBtn = e.target.closest('[data-cart-promo-apply]');
        if (!promoBtn.disabled) applyCartDiscount(promoBtn.getAttribute('data-cart-promo-apply'));
      }
    });
    cartDrawer.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.closest('[data-cart-discount-input]')) {
        e.preventDefault();
        applyCartDiscount(e.target.value);
      }
    });
  }

  function updateCartLine(key, qty) {
    var fd = new FormData();
    fd.append('updates[' + key + ']', qty);
    fetch('/cart/update.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderCart(cart); updateCartCount(cart.item_count); })
      .catch(function () {});
  }

  /* Apply / remove a cart-level discount code. The native /cart POST route
     consumes the `discount` param; we then re-fetch /cart.js so renderCart()
     reflects the new cart_level_discount_applications. Empty code clears it. */
  /* Per-product promo codes: surface the ones that apply to this cart and let the
     shopper apply the best single one in a tap. Shopify allows ONE code per cart, so
     we rank by money saved rather than pretending they all stack. */
  function updateCartPromos(cart) {
    if (!cartDrawer) return;
    var box = cartDrawer.querySelector('[data-cart-promos]');
    var list = cartDrawer.querySelector('[data-cart-promos-list]');
    if (!box || !list) return;

    var map = window.PHEEFEE_PROMOS || {};
    var items = cart.items || [];
    var found = {}, order = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var m = map[String(it.product_id)];
      if (!m) continue;
      var key = m.c;
      var save = Math.floor((it.line_price || 0) * m.p / 100);
      if (!found[key]) {
        found[key] = { code: m.c, pct: m.p, save: 0, names: [] };
        order.push(key);
      }
      found[key].save += save;
      found[key].names.push(it.product_title || '');
    }

    if (!order.length) { box.hidden = true; list.innerHTML = ''; return; }

    var rows = order.map(function (k) { return found[k]; });
    rows.sort(function (a, b) { return b.save - a.save; });

    var applied = appliedPromoCode();
    var html = '';
    for (var r = 0; r < rows.length; r++) {
      var row = rows[r];
      var isOn = applied.indexOf(row.code) === 0;
      html += '<div class="cart-promo' + (isOn ? ' is-applied' : '') + '">'
        + '<span class="cart-promo__txt">'
        + '<span class="cart-promo__name">' + row.names[0] + (row.names.length > 1 ? ' +' + (row.names.length - 1) + ' more' : '') + '</span>'
        + '<span class="cart-promo__code">' + row.code + '</span> -' + row.pct + '% '
        + '<span class="cart-promo__save">save ' + formatMoney(row.save) + '</span>'
        + '</span>'
        + '<button type="button" class="cart-promo__btn" data-cart-promo-apply="' + row.code + '"'
        + (isOn ? ' disabled' : '') + '>' + (isOn ? 'Applied' : 'Apply') + '</button>'
        + '</div>';
    }
    list.innerHTML = html;
    box.hidden = false;
  }

  /* Code discounts applied via /discount/CODE are reflected in cart.total_price
     but NOT in cart_level_discount_applications (that field is for automatic
     discounts only), so "already applied" detection reads the discount_code cookie. */
  function appliedPromoCode() {
    var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    return m ? decodeURIComponent(m[1] || '') : '';
  }

  function applyCartDiscount(code) {
    code = (code || '').trim();
    var req;
    if (code) {
      req = fetch('/discount/' + encodeURIComponent(code), { redirect: 'follow' });
    } else {
      var fd = new FormData();
      fd.append('discount', '');
      req = fetch('/cart', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } });
    }
    req
      .then(function () { return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }); })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderCart(cart); updateCartCount(cart.item_count); })
      .catch(function () {});
  }

  /* ---------------- Search overlay (full-screen takeover) ---------------- */
  function initSearchOverlay() {
    var panel = document.getElementById('search-overlay');
    var backdrop = document.getElementById('search-backdrop');
    if (!panel) return;
    var input = panel.querySelector('[data-search-input]');
    var results = panel.querySelector('[data-search-results]');
    var popular = panel.querySelector('[data-search-popular]');
    if (!input) return;
    var xhr = null, timer = null;

    function openSearch() {
      panel.hidden = false; if (backdrop) backdrop.hidden = false;
      void panel.offsetWidth;
      panel.classList.add('is-open'); if (backdrop) backdrop.classList.add('is-open');
      document.body.classList.add('search-open');
      input.focus();
    }
    function closeSearch() {
      panel.classList.remove('is-open'); if (backdrop) backdrop.classList.remove('is-open');
      document.body.classList.remove('search-open');
      setTimeout(function () { if (!panel.classList.contains('is-open')) { panel.hidden = true; if (backdrop) backdrop.hidden = true; } }, 380);
      input.value = ''; if (results) results.innerHTML = ''; if (popular) popular.hidden = false;
    }

    document.querySelectorAll('[data-search-open]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); openSearch(); });
    });
    panel.querySelectorAll('[data-search-close]').forEach(function (b) { b.addEventListener('click', closeSearch); });
    if (backdrop) backdrop.addEventListener('click', closeSearch);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && panel.classList.contains('is-open')) closeSearch(); });

    var form = panel.querySelector('form');
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      var q = input.value.trim();
      if (!q) return;
      window.location.href = form.getAttribute('action') + '?q=' + encodeURIComponent(q);
    });

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (!q) { if (results) results.innerHTML = ''; if (popular) popular.hidden = false; return; }
      if (popular) popular.hidden = true;
      timer = setTimeout(function () { fetchResults(q); }, 250);
    });

    function fetchResults(q) {
      if (xhr) xhr.abort();
      xhr = new XMLHttpRequest();
      xhr.open('GET', '/search?q=' + encodeURIComponent(q) + '&type=product&view=predictive', true);
      xhr.onload = function () {
        if (xhr.status !== 200 || !results) return;
        var doc = new DOMParser().parseFromString(xhr.responseText, 'text/html');
        var grid = doc.querySelector('.predictive-grid');
        if (grid && grid.children.length) {
          results.innerHTML = '';
          results.appendChild(grid);
          results.hidden = false;
          // Re-bind swatch + quick-add click handlers for newly injected product-cards
          if (typeof initCardSwatches === 'function') initCardSwatches();
          if (typeof initQuickAdd === 'function') initQuickAdd();
          if (typeof initBuyNow === 'function') initBuyNow();
        } else {
          results.innerHTML = '<p class="search-overlay__empty">No results for “' + q + '”</p>';
          results.hidden = false;
        }
      };
      xhr.send();
    }
  }

  /* ---------------- AJAX add to cart (opens drawer) ---------------- */
  function initAjaxCart() {
    var form = document.getElementById('product-form');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[data-add-to-cart]');
      var fd = new FormData(form);
      if (btn) { btn.disabled = true; setBtnLabel(btn, themeStrings.adding); }
      fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function () { return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }); })
        .then(function (r) { return r.json(); })
        .then(function (cart) { renderCart(cart); updateCartCount(cart.item_count); openCart(); })
        .catch(function () { form.submit(); })
        .then(function () { if (btn) { btn.disabled = false; setBtnLabel(btn, themeStrings.addToCart); } });
    });
  }

  /* ---------------- Product card: color swatch image swap ---------------- */
  function initCardSwatches() {
    document.querySelectorAll('.product-card').forEach(function (card) {
      if (card.dataset.swatchesBound) return;
      card.dataset.swatchesBound = '1';
      var img = card.querySelector('.product-card__media-primary');
      var links = card.querySelectorAll('a');
      card.querySelectorAll('.pc-swatch').forEach(function (sw) {
        sw.addEventListener('click', function (e) {
          e.preventDefault();
          card.querySelectorAll('.pc-swatch').forEach(function (s) { s.classList.remove('is-active'); });
          sw.classList.add('is-active');
          var variantId = sw.getAttribute('data-variant-id');
          var src = sw.getAttribute('data-img');
          if (img && src) {
            /* srcset overrides src in browsers — must clear it or the swap is invisible */
            img.src = src;
            img.removeAttribute('sizes');
            if (/_500x/.test(src)) {
              /* rebuild the same 300/500/700 ladder Shopify served originally */
              img.srcset = src.replace('_500x', '_300x') + ' 300w, ' + src + ' 500w, ' + src.replace('_500x', '_700x') + ' 700w';
            } else {
              img.srcset = '';
              img.removeAttribute('srcset');
            }
          }
          if (variantId) {
            links.forEach(function (a) {
              var base = a.href.split('?')[0];
              a.href = base + '?variant=' + variantId;
            });
          }
        });
      });
    });
  }

  /* ---------------- Product card: quick add ---------------- */
  function initQuickAdd() {
    document.querySelectorAll('[data-quick-add]').forEach(function (btn) {
      if (btn.dataset.qaBound) return;
      btn.dataset.qaBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var card = btn.closest('.product-card');
        if (!card) return;
        var vid = card.getAttribute('data-first-variant');
        if (!vid) return;
        btn.disabled = true;
        var prev = btn.textContent;
        btn.textContent = 'Adding…';
        var fd = new FormData();
        fd.append('id', vid);
        fd.append('quantity', 1);
        fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } })
          .then(function () { return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }); })
          .then(function (r) { return r.json(); })
          .then(function (cart) { renderCart(cart); updateCartCount(cart.item_count); openCart(); })
        .catch(function () {})
        .then(function () { btn.disabled = false; btn.textContent = prev; });
      });
    });
  }

  /* ---------------- Product card: buy now (add to cart + go to checkout) ---------------- */
  function initBuyNow() {
    document.querySelectorAll('[data-buy-now]').forEach(function (btn) {
      if (btn.dataset.bnBound) return;
      btn.dataset.bnBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var card = btn.closest('.product-card');
        if (!card) return;
        var vid = card.getAttribute('data-first-variant');
        if (!vid) return;
        btn.disabled = true;
        btn.textContent = '…';
        var fd = new FormData();
        fd.append('id', vid);
        fd.append('quantity', 1);
        fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } })
          .then(function () { window.location.href = '/checkout'; })
          .catch(function () { window.location.href = '/checkout'; });
      });
    });
  }

  /* ---------------- PDP desktop form: Buy now (add selected variant + go to checkout) ---------------- */
  function initPdpBuyNow() {
    document.querySelectorAll('[data-buy-now-pdp]').forEach(function (btn) {
      if (btn.dataset.bnPdpBound) return;
      btn.dataset.bnPdpBound = '1';
      btn.addEventListener('click', function () {
        var form = btn.closest('form');
        if (!form) return;
        var idInput = form.querySelector('input[name="id"]');
        if (!idInput || !idInput.value) return;
        var qtyInput = form.querySelector('input[name="quantity"]');
        var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        if (qty < 1) qty = 1;
        btn.disabled = true;
        btn.classList.add('is-loading');
        var fd = new FormData();
        fd.append('id', idInput.value);
        fd.append('quantity', qty);
        fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } })
          .then(function () { window.location.href = '/checkout'; })
          .catch(function () { window.location.href = '/checkout'; });
      });
    });
  }

  /* ---------------- PDP promo / discount code: tap to copy ---------------- */
  function initPromoCopy() {
    document.querySelectorAll('[data-promo-copy]').forEach(function (btn) {
      if (btn.dataset.promoBound) return;
      btn.dataset.promoBound = '1';
      btn.addEventListener('click', function () {
        var code = btn.getAttribute('data-code') || btn.textContent.trim();
        var prev = btn.textContent;
        var done = function () {
          btn.textContent = 'Copied!';
          btn.classList.add('is-copied');
          setTimeout(function () { btn.textContent = prev; btn.classList.remove('is-copied'); }, 1600);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done).catch(done);
        } else { done(); }
        window.dispatchEvent(new CustomEvent('promo:copied', { detail: { code: code } }));
      });
    });
  }

  /* ---------------- Sticky add-to-cart bar (product page) ---------------- */
  function initStickyAddBar() {
    var form = document.getElementById('product-form');
    var bar = document.querySelector('[data-sticky-addbar]');
    if (!form || !bar) return;
    document.body.classList.add('has-sticky-addbar');
    var stickyBtn = bar.querySelector('[data-sticky-add]');
    var stickyPrice = bar.querySelector('[data-sticky-price]');
    var priceEl = form.closest('.product') ? form.closest('.product').querySelector('[data-product-price]') : null;
    var atcBtn = form.querySelector('[data-add-to-cart]');

    function syncPrice() { if (stickyPrice && priceEl) stickyPrice.textContent = priceEl.textContent; }
    function syncDisabled() { if (stickyBtn && atcBtn) stickyBtn.disabled = atcBtn.disabled; }
    syncPrice();
    syncDisabled();

    var mqMobile = window.matchMedia('(max-width: 749px)');
    function onScroll() {
      if (mqMobile.matches) { bar.classList.add('is-visible'); return; }
      var rect = form.getBoundingClientRect();
      var show = rect.bottom < 90 && window.scrollY > 240;
      bar.classList.toggle('is-visible', show);
    }
    window.addEventListener('scroll', function () {
      window.requestAnimationFrame(onScroll);
    }, { passive: true });
    onScroll();

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }

    // keep sticky price + disabled state in sync when variants change
    var mo = priceEl ? new MutationObserver(function () { syncPrice(); syncDisabled(); }) : null;
    if (mo) mo.observe(priceEl, { childList: true, characterData: true, subtree: true });
    if (atcBtn) { var mo2 = new MutationObserver(syncDisabled); mo2.observe(atcBtn, { attributes: true, attributeFilter: ['disabled'] }); }
  }

  /* ---------------- Size chart toggle ---------------- */
  function initSizeChart() {
    document.querySelectorAll('[data-size-chart-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var target = document.getElementById(btn.getAttribute('data-size-chart-toggle'));
        if (!target) return;
        if (target.tagName === 'DETAILS') {
          target.open = true;
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          target.hidden = !target.hidden;
          if (!target.hidden) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });
  }

  /* ---------------- PDP tab switcher: accordion rows + reviews → panel stack ----------------
     Runs at EVERY width (the accordion is gone on desktop too). The <details>
     elements stay in the DOM — their bodies become the panels — but are forced
     open, because their <summary> row is hidden and CSS alone cannot expand a
     <details>. A brand-coloured pill slides under the active tab. */
  function initPdpTabs() {
    var tabs = document.querySelector('[data-pdp-tabs]');
    var wrap = document.querySelector('[data-pdp-panel-wrap]');
    if (!tabs || !wrap) return;

    var thumb = tabs.querySelector('[data-pdp-tabs-thumb]');

    /* Judge.me renders into #reviews, which lives OUTSIDE this section. Move that
       node into the panel stack — the id travels with it, so #reviews anchors and
       any external deep-link keep working. */
    var reviews = document.getElementById('reviews');
    var slot = wrap.querySelector('[data-pdp-panel="reviews"]');
    if (reviews && slot && reviews.parentNode !== slot) slot.appendChild(reviews);

    var btns = Array.prototype.slice.call(tabs.querySelectorAll('[data-pdp-tab]'));
    var panels = Array.prototype.slice.call(wrap.querySelectorAll('[data-pdp-panel]'));
    var current = 'details';

    /* Position the sliding pill. offsetLeft/offsetWidth are relative to the track,
       which is the nearest positioned ancestor — so this stays correct if the
       buttons themselves ever become scrollable. */
    function moveThumb(btn) {
      if (!thumb || !btn) return;
      thumb.style.width = btn.offsetWidth + 'px';
      thumb.style.transform = 'translateX(' + btn.offsetLeft + 'px)';
    }

    function activate(key) {
      var hit = false;
      panels.forEach(function (p) {
        var on = p.getAttribute('data-pdp-panel') === key;
        if (on) hit = true;
        p.classList.toggle('is-active', on);
      });
      if (!hit) return false;
      current = key;
      var activeBtn = null;
      btns.forEach(function (b) {
        var on = b.getAttribute('data-pdp-tab') === key;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) activeBtn = b;
      });
      moveThumb(activeBtn);
      return true;
    }

    // <summary> is hidden, so without `open` the panels would collapse to nothing.
    panels.forEach(function (p) { if (p.tagName === 'DETAILS') p.open = true; });

    btns.forEach(function (b) {
      b.addEventListener('click', function () { activate(b.getAttribute('data-pdp-tab')); });
    });

    /* The price-row "N reviews" link targets #reviews, which is now a hidden panel. */
    var reviewsLink = document.querySelector('.product__reviews');
    if (reviewsLink) {
      reviewsLink.addEventListener('click', function (e) {
        e.preventDefault();
        activate('reviews');
      });
    }

    /* "Size guide" toggles target #size-guide, also a hidden panel now. Capture
       phase, so the tab flips before initSizeChart's scroll handler runs. */
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var toggle = e.target.closest('[data-size-chart-toggle]');
      if (toggle && toggle.getAttribute('data-size-chart-toggle') === 'size-guide') activate('size');
    }, true);

    /* Button widths depend on the webfont, which may not be loaded yet at
       DOMContentLoaded — re-measure once fonts settle, on load, and on resize. */
    function realign() { moveThumb(tabs.querySelector('.pdp-tabs__btn.is-active')); }
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(realign);
    }
    window.addEventListener('resize', realign);
    window.addEventListener('load', realign);

    activate(current);
  }

  /* ---------------- Mobile nav drawer ---------------- */
  function initMobileDrawer() {
    var toggles = Array.prototype.slice.call(document.querySelectorAll('[data-nav-toggle]'));
    var drawer = document.querySelector('[data-mobile-drawer]');
    var overlay = document.querySelector('[data-drawer-overlay]');
    if (!toggles.length || !drawer) return;

    function open() {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      // Lets base.css stand the announcement bar down — its saturated colour showed
      // through the scrim in the sliver the drawer leaves uncovered.
      document.body.classList.add('drawer-open');
      toggles.forEach(function (t) { t.classList.add('is-active'); t.setAttribute('aria-expanded', 'true'); });
    }
    function close() {
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      toggles.forEach(function (t) { t.classList.remove('is-active'); t.setAttribute('aria-expanded', 'false'); });
    }

    toggles.forEach(function (t) {
      t.addEventListener('click', function () {
        drawer.classList.contains('is-open') ? close() : open();
      });
    });
    if (overlay) overlay.addEventListener('click', close);
    drawer.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', close); });
    var closeBtn = drawer.querySelector('[data-drawer-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  /* ---------------- Mega menu (desktop) ---------------- */
  function initMegaMenu() {
    var items = document.querySelectorAll('[data-mega-item]');
    items.forEach(function (item) {
      var trigger = item.querySelector('[data-mega-trigger]');
      var panel = item.querySelector('[data-mega-panel]');
      if (!trigger || !panel) return;

      function open() { trigger.setAttribute('aria-expanded', 'true'); panel.classList.add('is-open'); }
      function close() { trigger.setAttribute('aria-expanded', 'false'); panel.classList.remove('is-open'); }

      // Click: first click opens (for touch); second click (already open) navigates.
      trigger.addEventListener('click', function (e) {
        if (trigger.getAttribute('aria-expanded') === 'true') return; // let it navigate
        e.preventDefault();
        open();
      });
      // Keyboard toggle
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          trigger.getAttribute('aria-expanded') === 'true' ? close() : open();
        }
      });
      // Navigating within the panel closes it cleanly
      panel.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', close);
      });
      // Close on outside click
      document.addEventListener('click', function (e) {
        if (!item.contains(e.target)) close();
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        document.querySelectorAll('[data-mega-panel].is-open').forEach(function (p) {
          p.classList.remove('is-open');
          var t = p.parentNode.querySelector('[data-mega-trigger]');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  /* ---------------- Full-screen nav overlay (Collections etc.) ---------------- */
  function initNavOverlay() {
    var triggers = document.querySelectorAll('[data-overlay-trigger]');
    if (!triggers.length) return;
    var openPanel = null;

    function getTrigger(panel) {
      return document.querySelector('[data-overlay-trigger="' + (panel ? panel.id : '') + '"]');
    }
    function closeDrawer() {
      var drawer = document.querySelector('[data-mobile-drawer]');
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      var ov = document.querySelector('[data-drawer-overlay]'); if (ov) ov.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      var tg = document.querySelector('[data-nav-toggle]');
      if (tg) { tg.classList.remove('is-active'); tg.setAttribute('aria-expanded', 'false'); }
    }
    function open(panel, trigger) {
      panel.hidden = false;
      void panel.offsetWidth; // force reflow so the transition runs
      panel.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('nav-overlay-open');
      openPanel = panel;
      var c = panel.querySelector('[data-overlay-close]'); if (c) c.focus();
      closeDrawer();
    }
    function close(panel, trigger) {
      if (!panel) return;
      panel.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('nav-overlay-open');
      openPanel = null;
      setTimeout(function () { if (!panel.classList.contains('is-open')) panel.hidden = true; }, 380);
      if (trigger) trigger.focus();
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var panel = document.getElementById(trigger.getAttribute('data-overlay-trigger'));
        if (!panel) return;
        panel.classList.contains('is-open') ? close(panel, trigger) : open(panel, trigger);
      });
    });

    document.querySelectorAll('[data-overlay-close]').forEach(function (b) {
      b.addEventListener('click', function () {
        var p = b.closest('[data-overlay-panel]');
        close(p, getTrigger(p));
      });
    });

    document.querySelectorAll('[data-overlay-panel]').forEach(function (panel) {
      panel.addEventListener('click', function (e) {
        // close when clicking empty space (not on a card or the top bar)
        if (e.target === panel || (!e.target.closest('.nav-overlay__card') && !e.target.closest('.nav-overlay__bar'))) {
          close(panel, getTrigger(panel));
        }
      });
      panel.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', function () { close(panel, getTrigger(panel)); });
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openPanel) close(openPanel, getTrigger(openPanel));
    });
  }

  /* ---------------- Mobile drawer accordion ---------------- */
  function initDrawerAccordion() {
    document.querySelectorAll('[data-drawer-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var sub = btn.closest('.drawer-nav__group').querySelector('.drawer-nav__sub');
        if (!sub) return;
        var open = sub.hasAttribute('hidden');
        if (open) { sub.removeAttribute('hidden'); btn.setAttribute('aria-expanded', 'true'); }
        else { sub.setAttribute('hidden', ''); btn.setAttribute('aria-expanded', 'false'); }
      });
    });
  }

  var themeStrings = {
    addToCart: 'Add to cart',
    adding: 'Adding…',
    soldOut: 'Sold out',
    unavailable: 'Unavailable'
  };

  function initContactForm() {
    var form = document.querySelector('#contact-form');
    if (!form) return;
    var btn = form.querySelector('[data-contact-submit]');
    form.addEventListener('submit', function () {
      if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
    });
    // 提交成功后的状态面板：聚焦并滚动到可见
    var status = document.querySelector('[data-contact-status]');
    if (status) {
      status.focus();
      status.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // 再发一条：回到干净的表单
    var reset = document.querySelector('[data-contact-reset]');
    if (reset) {
      reset.addEventListener('click', function () {
        window.location.href = window.location.pathname;
      });
    }
  }

  function initArticleTOC() {
    var content = document.querySelector('.article-content');
    var toc = document.getElementById('article-toc');
    if (!content || !toc) return;
    var heads = content.querySelectorAll('h2');
    var list = toc.querySelector('.article-toc__list');
    if (!heads.length || !list) { toc.style.display = 'none'; return; }
    heads.forEach(function (h, i) {
      if (!h.id) h.id = 'section-' + i;
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'article-toc__link';
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      a.addEventListener('click', function (e) {
        e.preventDefault();
        h.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + h.id);
      });
      li.appendChild(a);
      list.appendChild(li);
    });
    var links = list.querySelectorAll('.article-toc__link');
    function onScroll() {
      var pos = window.scrollY + 120;
      var idx = 0;
      heads.forEach(function (h, i) { if (h.offsetTop <= pos) idx = i; });
      links.forEach(function (l, i) { l.classList.toggle('is-active', i === idx); });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initShareCopy() {
    var btn = document.querySelector('[data-copy-url]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var url = window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () {
          var t = btn.textContent;
          btn.textContent = 'Copied!';
          setTimeout(function () { btn.textContent = t; }, 1500);
        }).catch(function () {});
      }
    });
  }

  /* ---------------- Shop the Edit: infinite scroll + random order ---------------- */
  /* Fisher-Yates in-place shuffle. Used so the section shows the (whole-store)
     products in a randomized order instead of Shopify's default sort. */
  function shuffleArray(a) {
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = a[i]; a[i] = a[j]; a[j] = tmp;
    }
    return a;
  }

  function initEditInfiniteScroll() {
    var grid = document.querySelector('[data-edit-grid]');
    var sentinel = document.querySelector('[data-edit-sentinel]');
    if (!grid) return;
    var collection = grid.getAttribute('data-edit-collection');
    if (!collection) return;

    // How many cards the Liquid layer already rendered (== edit_per_page).
    // We must NOT reload these from the collection page or we'd duplicate them.
    var liquidCount = grid.querySelectorAll('.home-edit__cell').length;

    // Dedupe by product handle so a product never appears twice (guards against
    // any sort-order mismatch between the Liquid render and the fetched pages).
    var shownHandles = {};
    function handleOf(card) {
      var a = card.querySelector('a[href*="/products/"]');
      if (!a) return null;
      var m = (a.getAttribute('href') || '').match(/\/products\/([^\/\?\#]+)/);
      return m ? m[1] : null;
    }

    // Random order for the first batch — runs even if there is only one page,
    // so the section is never shown in Shopify's default (non-random) order.
    var initial = Array.prototype.slice.call(grid.querySelectorAll('.home-edit__cell'));
    if (initial.length) {
      shuffleArray(initial);
      initial.forEach(function (cell) {
        var c = cell.querySelector('.product-card');
        var h = c ? handleOf(c) : null;
        if (h) shownHandles[h] = 1;
        grid.appendChild(cell);
      });
    }

    if (!sentinel) return; // single page -> nothing more to load

    var page = 1;
    var firstLoad = true;
    var loading = false;
    var done = false;

    function finish() {
      if (sentinel && sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
    }

    function appendCell(card) {
      var h = handleOf(card);
      if (h && shownHandles[h]) return; // skip duplicate
      if (h) shownHandles[h] = 1;
      // normalize links to homepage style (strip /collections/<handle>/)
      card.querySelectorAll('a').forEach(function (a) {
        a.href = a.href.replace('/collections/' + collection + '/products/', '/products/');
      });
      var cell = document.createElement('div');
      cell.className = 'home-edit__cell home-reveal';
      cell.appendChild(card);
      grid.appendChild(cell);
      requestAnimationFrame(function () { cell.classList.add('is-visible'); });
    }

    function fetchPage(p) {
      var url = '/collections/' + encodeURIComponent(collection) + '?page=' + p;
      return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          return Array.prototype.slice.call(doc.querySelectorAll('#product-grid .product-card'));
        });
    }

    function loadMore() {
      if (loading || done) return;
      loading = true;
      sentinel.classList.add('is-loading');

      // The first JS load must consume the TAIL of collection page 1 (the
      // products the Liquid layer already rendered are [0, liquidCount)),
      // so we never skip or duplicate anything even though the collection
      // template paginates at its own (larger) page size.
      var p = firstLoad ? 1 : (page += 1);
      firstLoad = false;

      fetchPage(p)
        .then(function (cards) {
          if (!cards.length) { done = true; finish(); return; }
          if (p === 1) cards = cards.slice(liquidCount); // skip already-shown
          if (!cards.length) { // tiny collection edge case
            done = true; finish(); return;
          }
          shuffleArray(cards); // random order within each loaded batch
          cards.forEach(appendCell);
          if (typeof initCardSwatches === 'function') initCardSwatches();
          if (typeof initQuickAdd === 'function') initQuickAdd();
          if (typeof initBuyNow === 'function') initBuyNow();
        })
        .catch(function () { done = true; finish(); })
        .then(function () { loading = false; sentinel.classList.remove('is-loading'); });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) loadMore(); });
      }, { rootMargin: '500px 0px' });
      io.observe(sentinel);
    } else {
      window.addEventListener('scroll', function () {
        var rect = sentinel.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 500) loadMore();
      }, { passive: true });
    }
  }

  /* ---------------- Homepage editorial animations ---------------- */
  function initHomepage() {
    var reveals = document.querySelectorAll('.home-reveal');
    if (!reveals.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          var delay = en.target.dataset.delay || 0;
          setTimeout(function () { en.target.classList.add('is-visible'); }, +delay);
          io.unobserve(en.target);
        }
      });
    }, { threshold: .12, rootMargin: '0px 0px -80px 0px' });
    reveals.forEach(function (el) { io.observe(el); });

    // Product rails — drag-to-scroll + progress bar (PC)
    function bindRail(section) {
      var win = section.querySelector('[data-home-rail]');
      if (!win) return;

      // drag-to-scroll directly on the rail
      var isDown = false, startX = 0, startScroll = 0;
      win.addEventListener('mousedown', function (e) {
        isDown = true; startX = e.pageX - win.offsetLeft; startScroll = win.scrollLeft;
      });
      win.addEventListener('mouseleave', function () { isDown = false; });
      win.addEventListener('mouseup', function () { isDown = false; });
      win.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - win.offsetLeft;
        win.scrollLeft = startScroll - (x - startX) * 1.4;
      });

      // progress bar — reflects scroll position + click/drag to slide
      var progress = section.querySelector('[data-rail-progress]');
      var bar = progress ? progress.querySelector('[data-rail-bar]') : null;
      if (!progress || !bar) return;

      function maxScroll() { return win.scrollWidth - win.clientWidth; }
      function update() {
        var max = maxScroll();
        if (max <= 1) { progress.style.visibility = 'hidden'; return; }
        progress.style.visibility = 'visible';
        var viewRatio = win.clientWidth / win.scrollWidth;
        var w = Math.max(0.12, viewRatio) * 100;
        var left = (win.scrollLeft / max) * (100 - w);
        bar.style.width = w + '%';
        bar.style.left = left + '%';
      }
      win.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);

      var dragging = false;
      function setFromX(clientX) {
        var rect = progress.getBoundingClientRect();
        var p = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        var viewRatio = Math.min(1, win.clientWidth / win.scrollWidth);
        var w = Math.max(0.12, viewRatio);
        var leftRatio = Math.min(1 - w, Math.max(0, p - w / 2));
        win.scrollLeft = leftRatio * maxScroll();
      }
      progress.addEventListener('mousedown', function (e) { dragging = true; setFromX(e.clientX); e.preventDefault(); });
      window.addEventListener('mousemove', function (e) { if (dragging) setFromX(e.clientX); });
      window.addEventListener('mouseup', function () { dragging = false; });
      update();
    }
    document.querySelectorAll('.home-rail').forEach(bindRail);
  }

  /* ---------------- Back to top (site-wide) ---------------- */
  function initBackToTop() {
    var btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    var ticking = false;
    function update() {
      if (window.scrollY > 400) btn.classList.add('is-visible');
      else btn.classList.remove('is-visible');
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    btn.addEventListener('click', function () {
      smoothScrollTop(800);
      // Move keyboard focus to the top of the page for screen-reader users.
      // Focus the main region (tabindex="-1", no visible outline) rather than
      // the off-screen skip link — focusing the skip link forces it visible.
      var main = document.getElementById('MainContent');
      if (main) main.focus({ preventScroll: true });
    });
    update();

    function smoothScrollTop(duration) {
      var start = window.scrollY || window.pageYOffset;
      if (start === 0) return;
      // respect reduced-motion preference: jump instantly
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.scrollTo(0, 0);
        return;
      }
      var startTime = null;
      function step(ts) {
        if (startTime === null) startTime = ts;
        var elapsed = ts - startTime;
        var t = Math.min(elapsed / duration, 1);
        // easeInOutCubic — gentle start and finish, no abrupt stop
        var eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        window.scrollTo(0, Math.round(start * (1 - eased)));
        if (elapsed < duration) window.requestAnimationFrame(step);
        else window.scrollTo(0, 0);
      }
      window.requestAnimationFrame(step);
    }
  }

  /* ---------------- PDP mobile action TabBar + variant sheet ---------------- */
  function initPdpActionBar() {
    var bar = document.querySelector('[data-pdp-actionbar]');
    if (!bar) return;
    bar.removeAttribute('hidden');

    var form = document.getElementById('product-form');
    if (!form) return;
    var saveBtn = bar.querySelector('[data-pdp-save]');
    var addBtn = bar.querySelector('[data-pdp-add]');
    var buyBtn = bar.querySelector('[data-pdp-buy]');

    var sheet = document.querySelector('[data-variant-sheet]');
    var jsonEl = form.querySelector('[data-product-json]');
    var productJson = jsonEl ? JSON.parse(jsonEl.textContent) : null;
    var hiddenId = form.querySelector('input[name="id"]');
    var defaultVariantId = hiddenId ? parseInt(hiddenId.value, 10) : null;
    var mode = 'add';
    var lastFocus = null;

    var confirmBtn = sheet ? sheet.querySelector('[data-variant-confirm]') : null;

    /* Resolve the variant the sheet currently points at:
       -> a variant object when the combination exists
       -> undefined when the shopper hasn't picked every option yet
       -> null when the combination doesn't exist at all */
    function sheetVariant() {
      if (!sheet) return null;
      var inputs = sheet.querySelectorAll('[data-sheet-input]');
      if (!inputs.length) {
        if (!productJson) return null;
        for (var i = 0; i < productJson.variants.length; i++) {
          if (productJson.variants[i].id === defaultVariantId) return productJson.variants[i];
        }
        return null;
      }
      var selected = {}, names = {};
      inputs.forEach(function (inp) {
        names[inp.getAttribute('data-option-name')] = 1;
        if (inp.checked) selected[inp.getAttribute('data-option-name')] = inp.getAttribute('data-value');
      });
      if (Object.keys(selected).length < Object.keys(names).length) return undefined;
      if (!productJson) return null;
      var match = productJson.variants.filter(function (v) {
        return productJson.options.every(function (opt, idx) { return selected[opt] === v.options[idx]; });
      })[0];
      return match || null;
    }

    /* Keep the sheet's Confirm button honest — a sold-out size greys it out
       instead of letting the shopper tap through and hit an alert. */
    /* The phone's inline pickers are hidden, so this summary row is its only door
       back into the option pickers. */
    var summaryBtn = document.querySelector('[data-open-variant-sheet]');
    var summaryImg = document.querySelector('[data-spec-summary-img]');
    var summaryTitle = document.querySelector('[data-spec-summary-title]');

    var previewCard = sheet ? sheet.querySelector('[data-sheet-preview]') : null;
    var previewMeta = sheet ? sheet.querySelector('.pdp-variant-sheet__meta') : null;
    var previewImg = sheet ? sheet.querySelector('[data-sheet-preview-img]') : null;
    var sheetSku = sheet ? sheet.querySelector('[data-sheet-sku]') : null;
    var previewTitle = sheet ? sheet.querySelector('[data-sheet-preview-title]') : null;
    var previewPrice = sheet ? sheet.querySelector('[data-sheet-preview-price]') : null;
    // Clear the one-shot animation classes once they have played, so the next
    // selection can trigger them again.
    [previewCard, previewMeta, previewImg].forEach(function (el) {
      if (!el) return;
      el.addEventListener('animationend', function () {
        el.classList.remove('is-updated', 'is-swapped');
      });
    });

    /* Keep the sheet's picture, variant name and price in step with the options.
       On a phone the gallery is already off-screen when the sheet opens, so this
       is the only place the shopper can see what they are actually choosing. */
    function refreshSheetPreview() {
      if (!sheet) return;
      var v = sheetVariant();
      if (!v) return; // nothing fully selected / no such combination — keep the last good state
      var img = v.featured_image || (productJson && productJson.main_image) || null;
      var swapped = false;
      if (previewImg && img && previewImg.getAttribute('src') !== img) {
        previewImg.src = img;
        swapped = true;
      }
      if (previewTitle) {
        previewTitle.textContent = v.title
          ? v.title.split(' / ').slice(0, 2).join(' · ')
          : '—';
      }
      if (previewPrice && v.price != null) previewPrice.textContent = formatMoney(v.price);
      // The SKU row lives in the sheet now; keep it on the selected variant.
      if (sheetSku && v.sku) sheetSku.textContent = v.sku;

      // Visible confirmation that the pick registered. Many colourways reuse one
      // photo across sizes, so without this a size change looks like a no-op.
      replay(previewCard, 'is-updated');
      replay(previewMeta, 'is-updated');
      if (swapped) replay(previewImg, 'is-swapped');

      refreshSpecSummary(v);
    }

    /* The phone's inline pickers are hidden (they duplicated this sheet), so this
       row is what tells the shopper what they currently have and how to change it. */
    function refreshSpecSummary(v) {
      if (!v || !v.title) return;
      if (summaryTitle) summaryTitle.textContent = v.title.split(' / ').slice(0, 2).join(' · ');
      var img = v.featured_image || (productJson && productJson.main_image) || null;
      if (summaryImg && img && summaryImg.getAttribute('src') !== img) summaryImg.src = img;
    }

    /* Restart a one-shot CSS animation: drop the class, force a reflow, re-add. */
    function replay(el, cls) {
      if (!el) return;
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
    }

    function refreshSheetConfirm() {
      refreshSheetPreview();
      if (!confirmBtn) return;
      var v = sheetVariant();
      var available = (v === undefined) ? true : (v ? !!v.available : false);
      confirmBtn.disabled = !available;
      if (available) confirmBtn.removeAttribute('aria-disabled');
      else confirmBtn.setAttribute('aria-disabled', 'true');
      confirmBtn.textContent = available
        ? (mode === 'buy' ? 'Buy now' : 'add to cart')
        : themeStrings.soldOut;
    }

    /* Save / wishlist — local toggle persisted in localStorage */
    if (saveBtn) {
      var pid = bar.getAttribute('data-product-id');
      var WKEY = 'phefee_wishlist';
      function getWish() { try { return JSON.parse(localStorage.getItem(WKEY)) || []; } catch (e) { return []; } }
      function setWish(a) { try { localStorage.setItem(WKEY, JSON.stringify(a)); } catch (e) {} }
      if (getWish().indexOf(pid) !== -1) {
        saveBtn.classList.add('is-active');
        saveBtn.setAttribute('aria-pressed', 'true');
      }
      saveBtn.addEventListener('click', function () {
        var list = getWish();
        var i = list.indexOf(pid);
        if (i === -1) { list.push(pid); saveBtn.classList.add('is-active'); saveBtn.setAttribute('aria-pressed', 'true'); }
        else { list.splice(i, 1); saveBtn.classList.remove('is-active'); saveBtn.setAttribute('aria-pressed', 'false'); }
        setWish(list);
      });
    }

    /* Variant sheet open/close */
    function openSheet(m) {
      if (!sheet) return;
      mode = m;
      syncSheetAria();
      refreshSheetConfirm();
      sheet.classList.add('is-open');
      sheet.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      // Shopify Inbox's launcher floats over the sheet's quantity row and its bubble
      // lives in a shadow root, so it can't be repositioned from here — hide the
      // whole widget while the sheet is up (see base.css).
      document.body.classList.add('pdp-sheet-open');
      lastFocus = document.activeElement;
    }
    function closeSheet() {
      if (!sheet) return;
      sheet.classList.remove('is-open');
      setTimeout(function () { sheet.setAttribute('hidden', ''); }, 260);
      document.body.style.overflow = '';
      document.body.classList.remove('pdp-sheet-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    /* The sheet sits OUTSIDE <form>, so syncAria() — which scopes its query to the
       form — never reached these inputs. Without aria-checked the option pills had
       no selected highlight at all, which made the sheet feel broken. */
    function syncSheetAria() {
      if (!sheet) return;
      sheet.querySelectorAll('.variant-value').forEach(function (label) {
        var inp = label.querySelector('[data-sheet-input]');
        label.setAttribute('aria-checked', inp && inp.checked ? 'true' : 'false');
      });
    }

    /* Quantity stepper — it lives in the sheet now, and mirrors into the main form so
       both the ajax add-to-cart and the Buy-now path post the chosen amount. */
    var sheetQty = 1;
    var qtyValueEl = sheet ? sheet.querySelector('[data-sheet-qty-value]') : null;
    var qtyDecBtn = sheet ? sheet.querySelector('[data-sheet-qty-dec]') : null;
    var qtyIncBtn = sheet ? sheet.querySelector('[data-sheet-qty-inc]') : null;
    var formQty = form.querySelector('input[name="quantity"]');

    function setSheetQty(n) {
      sheetQty = Math.max(1, Math.min(99, n));
      if (qtyValueEl) qtyValueEl.textContent = sheetQty;
      if (formQty) formQty.value = sheetQty;
      if (qtyDecBtn) qtyDecBtn.disabled = sheetQty <= 1;
    }
    if (qtyDecBtn) qtyDecBtn.addEventListener('click', function () { setSheetQty(sheetQty - 1); });
    if (qtyIncBtn) qtyIncBtn.addEventListener('click', function () { setSheetQty(sheetQty + 1); });
    setSheetQty(1);

    if (sheet) {
      sheet.querySelectorAll('[data-variant-sheet-close]').forEach(function (el) {
        el.addEventListener('click', closeSheet);
      });
      sheet.querySelectorAll('[data-sheet-input]').forEach(function (inp) {
        inp.addEventListener('change', function () {
          var lbl = sheet.querySelector('[data-sheet-selected="' + inp.getAttribute('data-option-name') + '"]');
          if (lbl) lbl.textContent = inp.getAttribute('data-value');
          syncSheetAria();
          refreshSheetConfirm();
        });
      });
      syncSheetAria();
    }
    if (addBtn) addBtn.addEventListener('click', function () { openSheet('add'); });
    if (buyBtn) buyBtn.addEventListener('click', function () { openSheet('buy'); });
    if (summaryBtn) summaryBtn.addEventListener('click', function () { openSheet('add'); });

    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        var sheetInputs = sheet.querySelectorAll('[data-sheet-input]');
        var variantId = null;

        if (sheetInputs.length) {
          var selected = {};
          sheetInputs.forEach(function (inp) { if (inp.checked) selected[inp.getAttribute('data-option-name')] = inp.getAttribute('data-value'); });
          var optNames = {};
          sheetInputs.forEach(function (inp) { optNames[inp.getAttribute('data-option-name')] = 1; });
          if (Object.keys(selected).length < Object.keys(optNames).length) { alert('Please select all options.'); return; }
          var match = productJson.variants.filter(function (v) {
            return productJson.options.every(function (opt, idx) { return selected[opt] === v.options[idx]; });
          })[0];
          if (!match) { alert('This combination is unavailable.'); return; }
          if (!match.available) { alert('This combination is out of stock.'); return; }
          variantId = match.id;
        } else {
          variantId = defaultVariantId;
        }
        if (!variantId) { alert('Please choose a variant.'); return; }

        /* sync the main form to the chosen variant */
        if (hiddenId) hiddenId.value = variantId;
        if (productJson && productJson.options) {
          productJson.options.forEach(function (opt) {
            var val = sheetInputs.length ? selected[opt] : null;
            if (!val) return;
            var r = form.querySelector('input[data-option-name="' + opt + '"][data-value="' + val + '"]');
            if (r) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); }
          });
        }

        closeSheet();

        if (mode === 'buy') {
          var fd = new FormData();
          fd.append('id', variantId);
          fd.append('quantity', sheetQty);
          fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } })
            .then(function () { window.location.href = '/checkout'; })
            .catch(function () { window.location.href = '/checkout'; });
        } else {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet && sheet.classList.contains('is-open')) closeSheet();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initEditInfiniteScroll();
    initVariantPickers();
    alignSpecLabels();
    window.addEventListener('resize', alignSpecLabels);
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(alignSpecLabels);
    }
    initCartDrawer();
    initSearchOverlay();
    initAjaxCart();
    initSizeChart();
    initCardSwatches();
    initQuickAdd();
    initBuyNow();
    initPdpBuyNow();
    initPromoCopy();
    initStickyAddBar();
    initPdpTabs();
    initPdpActionBar();
    initMobileDrawer();
    initMegaMenu();
    initNavOverlay();
    initDrawerAccordion();
    initContactForm();
    initArticleTOC();
    initShareCopy();
    initHomepage();
    initBackToTop();
    initHeaderBack();
  });

  function initHeaderBack() {
    var btn = document.querySelector('[data-header-back]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var ref = document.referrer;
      var sameStore = ref && ref.indexOf(location.host) !== -1;
      if (sameStore) {
        // came from within the store (collection / search / home) — return to that level
        if (window.history.length > 1) {
          window.history.back();
        } else {
          window.location.href = ref; // opened in a new tab, no in-tab history to go back to
        }
      } else {
        // no in-store previous level (direct open / external link / empty) -> home
        window.location.href = '/';
      }
    });
  }

  /* ---------------- Public hook: "Complete the look" multi-add ----------------
     The PDP outfit module needs to drop several variants into the cart at once
     and then reuse the drawer rendering (renderCart + count + openCart), which
     all live in this closure. Expose a single narrow entry point instead of
     duplicating the cart logic in the section. */
  window.phefeeCart = {
    addMany: function (ids) {
      var items = (ids || []).filter(Boolean).map(function (id) {
        return { id: Number(id), quantity: 1 };
      });
      if (!items.length) return Promise.reject(new Error('no items'));
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json(); })
        .then(function () { return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }); })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          renderCart(cart);
          updateCartCount(cart.item_count);
          openCart();
          return cart;
        });
    }
  };
})();
